import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useTSQLocation as useLocQuery,
  useTSQLocationList,
  useTSMCreateLocation,
  useTSMUpdateLocation,
} from "../../hooks/useTSLocations";
import { locationService } from "../../services/svc_locations";
import { ListRequestFilter } from "../../types/common/commonTypes";
import {
  LocationCreate,
  LocationUpdate,
  LocationFull,
  LOCATION_TYPES,
  LocationType,
} from "../../types/locationTypes";
import stringUtils from "../../utils/stringUtils";

interface LocationFormData {
  name: string;
  location_type: string;
  parent_location_id?: string;
  facility_code?: string;
  description?: string;
  is_default_receiving_bin: boolean;
}

// Validation schema
const locationSchema = yup.object({
  name: yup.string().required("Location name is required"),
  location_type: yup
    .string()
    .required("Location type is required")
    .oneOf(LOCATION_TYPES),
  parent_location_id: yup.string().optional(),
  facility_code: yup.string().optional(),
  description: yup.string().optional(),
  is_bin: yup.bool().required(),
  is_default_receiving_bil: yup.bool().required(),
});

export const PageLocationForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const isViewMode = formMode === "view";
  const isEditMode = Boolean(id) && formMode === "edit";
  const isCreateMode = !id && formMode === "create";

  const {
    data: thisLocData,
    isLoading: thisLocLoading,
    error: thisLocError,
  } = useLocQuery(id || "");

  let allLocFilters: ListRequestFilter[] = [];
  if (id) {
    allLocFilters.push({ field: "id", operator: "!=", value: id });
    allLocFilters.push({
      field: "parent_location_id",
      operator: "!=",
      value: id,
    });
  }
  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
    isPlaceholderData,
  } = useTSQLocationList({
    limit: 10000,
    active_status: "active",
    filters: [allLocFilters],
  });

  const createLocMutation = useTSMCreateLocation();
  const updateLocMutation = useTSMUpdateLocation();

  const allLocs = locsResponse?.items || [];

  const [error, setError] = useState<string | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [availLocs, setAvailLocs] = useState<LocationFull[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
  } = useForm<LocationFormData>({
    resolver: yupResolver(locationSchema) as any,
    defaultValues: {
      name: "",
      location_type: "",
      parent_location_id: "",
      facility_code: "",
      description: "",
      is_default_receiving_bin: false,
    },
  });

  const switchToViewMode = () => {
    navigate(`/locations/form?mode=view&id=${id}`);
  };

  const switchToEditMode = () => {
    navigate(`/locations/form?mode=edit&id=${id}`);
  };

  const switchToCreateMode = () => {
    navigate(`/locations/form?mode=create`);
  };

  useEffect(() => {
    if (thisLocData && (isEditMode || isViewMode)) {
      setSelectedParentId(thisLocData.parent_location?.id || null);
      reset({
        name: thisLocData.name,
        location_type: thisLocData.location_type,
        parent_location_id: thisLocData.parent_location?.id,
        facility_code: thisLocData.facility_code,
        description: thisLocData.description,
        is_default_receiving_bin: thisLocData.is_default_receiving_bin,
      });
    }
  }, [thisLocData, isEditMode, isViewMode, reset, allLocs]);

  useEffect(() => {
    if (locsResponse?.items) {
      setAvailLocs(locsResponse.items);
    }
  }, [locsResponse?.items]);

  const onSubmit = async (data: LocationFormData) => {
    try {
      setError(null);

      if (isCreateMode) {
        const locPayload: LocationCreate = {
          name: data.name,
          location_type: data.location_type as LocationType,
          parent_location_id:
            data.parent_location_id === ""
              ? undefined
              : data.parent_location_id,
          facility_code: data.facility_code,
          description: data.description,
          is_default_receiving_bin: data.is_default_receiving_bin,
        };
        const new_loc = await createLocMutation.mutateAsync(locPayload);
        navigate(`/locations/form?mode=view&id=${new_loc.id}`);
      } else {
        if (!id) {
          throw new Error("Location ID is required for update");
        }
        const locPayload: LocationUpdate = {
          id: id,
          name: data.name,
          location_type: data.location_type as LocationType,
          parent_location_id:
            data.parent_location_id === ""
              ? undefined
              : data.parent_location_id,
          facility_code: data.facility_code,
          description: data.description,
        };
        await updateLocMutation.mutateAsync({ id: id, data: locPayload });
        navigate(`/locations/form?mode=view&id=${id}`);
      }
    } catch (err: any) {
      console.error("Failed to save Location:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save Location",
      );
    }
  };

  const handleCancel = () => {
    navigate("/locations");
  };

  const getParentLocationDisplay = (parentId: string | null) => {
    if (!parentId) return null;
    const parent = allLocs.find((loc) => loc.id === parentId);
    return parent ? `${parent.name} (${parent.location_type})` : parentId;
  };

  const isLoading = thisLocLoading || locsLoading;
  const isSaving = createLocMutation.isPending || updateLocMutation.isPending;

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          variant="outlined"
        >
          Back to Locations
        </Button>
        <Typography variant="h4" component="h1">
          {isCreateMode
            ? "Create Location"
            : isViewMode
              ? `View Location: ${thisLocData?.name || ""}`
              : `Edit Location: ${thisLocData?.name || ""}`}
        </Typography>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={(e) => {
            console.log(getValues());
          }}
        >
          Print
        </Button>
      </Stack>

      {(error || thisLocError || locsError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error ||
            thisLocError?.message ||
            locsError?.message ||
            "An unkown error occurred"}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Location Name *"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder="e.g., Main Warehouse, Zone A, Aisle 1, Bin A1-01"
                  />
                )}
              />

              <Controller
                name="location_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={isViewMode}>
                    <InputLabel>Location Type</InputLabel>
                    <Select {...field} label="Location Type">
                      {LOCATION_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {stringUtils.capFirstLetter(
                            type.replace(/[_]/g, " "),
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.location_type && (
                      <FormHelperText>
                        {errors.location_type.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            {/* Hierarchy */}
            <Typography variant="h6" gutterBottom>
              Location Hierarchy
            </Typography>

            <Controller
              name="parent_location_id"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  disabled={isViewMode}
                  error={!!errors.parent_location_id}
                >
                  <InputLabel>Parent Location</InputLabel>
                  <Select
                    {...field}
                    label="Parent Location"
                    onChange={(e) => {
                      field.onChange(e);
                      setSelectedParentId(e.target.value);
                    }}
                  >
                    {availLocs.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.parent_location_id && (
                    <FormHelperText>
                      {errors.parent_location_id.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {selectedParentId && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Parent:</strong>{" "}
                  {getParentLocationDisplay(selectedParentId)}
                </Typography>
              </Alert>
            )}

            {/* Configuration */}
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>

            <Controller
              name="is_default_receiving_bin"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={
                        (!(formMode === "create") && !(formMode === "edit")) ||
                        !selectedParentId
                      }
                    />
                  }
                  label="Default Receiving BIN?"
                />
              )}
            />

            <Controller
              name="facility_code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Facility Code"
                  fullWidth
                  disabled={isViewMode}
                  error={!!errors.facility_code}
                  helperText={
                    errors.facility_code?.message ||
                    "Internal facility identifier"
                  }
                  placeholder="e.g., WH001, DC-EAST"
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isViewMode}
                  error={!!errors.description}
                  helperText={
                    errors.description?.message ||
                    "Additional notes about this location"
                  }
                  placeholder="Special handling requirements, access restrictions, etc."
                />
              )}
            />

            {/* Form Actions */}
            {!isViewMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isSubmitting || isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  disabled={isSubmitting || isSaving}
                >
                  {isSaving
                    ? "Saving..."
                    : isCreateMode
                      ? "Create Location"
                      : "Update Location"}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
